import { useEffect, useState } from "react";

import type { NotesData } from "../types";

type UseNotesStateParams = {
	notesData: NotesData;
	isNotesLoading: boolean;
};

type UseNotesStateReturn = {
	publicNoteContent: string;
	setPublicNoteContent: (content: string) => void;
	privateNoteContent: string;
	setPrivateNoteContent: (content: string) => void;
	isPrivateNotesVisible: boolean;
	setIsPrivateNotesVisible: (visible: boolean) => void;
	isPublicContentLoaded: boolean;
	isPrivateContentLoaded: boolean;
};

/**
 * Hook for managing notes content state and synchronization.
 * Handles initial content loading, dirty state tracking, and visibility.
 * @param notesData - Parsed notes data from useNotesData hook
 * @param isNotesLoading - Whether notes are currently loading
 */
export const useNotesState = ({ notesData, isNotesLoading }: UseNotesStateParams): UseNotesStateReturn => {
	const [isPrivateNotesVisible, setIsPrivateNotesVisible] = useState(false);

	const [publicNoteContent, setPublicNoteContent] = useState("");
	const [privateNoteContent, setPrivateNoteContent] = useState("");

	const [publicContentLoaded, setPublicContentLoaded] = useState(false);
	const [privateContentLoaded, setPrivateContentLoaded] = useState(false);

	const publicNoteId = notesData.primaryPublicNotes?._id ?? null;
	const privateNoteId = notesData.userPrivateNotes?._id ?? null;

	const hasPrivateNotes = Boolean(notesData.userPrivateNotes?.content?.trim());

	useEffect(() => {
		if (hasPrivateNotes) {
			setIsPrivateNotesVisible(true);
		}
	}, [hasPrivateNotes]);

	useEffect(() => {
		setPublicContentLoaded(false);
		setPublicNoteContent("");
	}, [publicNoteId]);

	useEffect(() => {
		setPrivateContentLoaded(false);
		setPrivateNoteContent("");
	}, [privateNoteId]);

	useEffect(() => {
		if (!isNotesLoading && notesData.primaryPublicNotes?.content !== undefined && !publicContentLoaded) {
			setPublicNoteContent(notesData.primaryPublicNotes.content);
			setPublicContentLoaded(true);
		}
	}, [isNotesLoading, notesData.primaryPublicNotes?.content, publicContentLoaded]);

	useEffect(() => {
		if (!isNotesLoading && notesData.userPrivateNotes?.content !== undefined && !privateContentLoaded) {
			setPrivateNoteContent(notesData.userPrivateNotes.content);
			setPrivateContentLoaded(true);
		}
	}, [isNotesLoading, notesData.userPrivateNotes?.content, privateContentLoaded]);

	return {
		publicNoteContent,
		setPublicNoteContent,
		privateNoteContent,
		setPrivateNoteContent,
		isPrivateNotesVisible,
		setIsPrivateNotesVisible,
		isPublicContentLoaded: publicContentLoaded,
		isPrivateContentLoaded: privateContentLoaded,
	};
};
